import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { FileText, PlusCircle, Sparkles, Users, LogOut, Menu, CreditCard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PaymentIssueBanner } from "@/components/PaymentIssueBanner";

const NAV = [
  { to: "/", label: "Session notes", icon: FileText, end: true, id: "nav-notes" },
  { to: "/notes/new", label: "New SOAP note", icon: PlusCircle, id: "nav-new-note" },
  { to: "/polish", label: "Polish a note", icon: Sparkles, id: "nav-polish" },
  { to: "/clients", label: "Clients & goals", icon: Users, id: "nav-clients" },
  { to: "/billing", label: "Plan & account", icon: CreditCard, id: "nav-billing" },
];

export function Brand({ light = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl grid place-items-center font-display font-bold text-lg ${light ? "bg-white text-primary" : "bg-primary text-white"}`}>
        B
      </div>
      <div className="leading-tight">
        <div className={`font-display font-semibold text-[15px] ${light ? "text-white" : "text-slate-900"}`}>Beyond Boundaries</div>
        <div className={`text-[11px] uppercase tracking-[0.18em] ${light ? "text-teal-100" : "text-amber-600"}`}>ABA Scribe</div>
      </div>
    </div>
  );
}

function SideNav({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  useEffect(() => {
    const f = () => setTick((t) => t + 1);
    window.addEventListener("bb-url", f);
    return () => window.removeEventListener("bb-url", f);
  }, []);
  return (
    <div className="flex h-full flex-col gap-8 p-6">
      <Brand />
      <nav className="flex flex-col gap-1">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={id}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive && !(to === "/notes/new" && window.location.pathname !== to) ? "bg-primary text-white shadow-sm" : "text-slate-600 hover:bg-secondary hover:text-primary"
              }`
            }
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto rounded-xl border bg-secondary/60 p-4">
        <div className="text-sm font-semibold text-slate-900" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        <span className="mt-2 inline-block font-mono text-[11px] font-semibold px-2 py-0.5 rounded bg-white text-teal-800 border border-teal-200" data-testid="current-user-role">
          {user?.role}
        </span>
        {user?.access?.reason === "trial" && (
          <NavLink to="/billing" onClick={onNavigate} className="mt-2 block text-xs text-amber-700 hover:underline" data-testid="trial-badge">
            {user.access.trial_days_left} trial day{user.access.trial_days_left === 1 ? "" : "s"} left · Choose plan
          </NavLink>
        )}
        {user?.access && !user.access.has_access && (
          <NavLink to="/billing" onClick={onNavigate} className="mt-2 block text-xs text-red-600 hover:underline" data-testid="trial-ended-badge">Trial ended · Choose plan</NavLink>
        )}
        {user?.access?.group && <div className="mt-2 text-xs text-slate-500 truncate" data-testid="sidebar-team-name">{user.access.group.name}</div>}
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 w-full justify-start text-slate-600"
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="no-print hidden lg:block border-r bg-white sticky top-0 h-screen">
        <SideNav />
      </aside>
      <header className="no-print lg:hidden flex items-center justify-between border-b bg-white px-4 py-3 sticky top-0 z-30">
        <Brand />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <SideNav onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="p-4 sm:p-6 lg:p-10 max-w-[1500px] w-full">
        <PaymentIssueBanner />
        <Outlet />
      </main>
    </div>
  );
}
